import {
  Directive,
  DoCheck,
  EmbeddedViewRef,
  Inject,
  Input,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { VALIDATION_CONTEXT, ValidationContextDirective } from './validation-context.directive';

export type TypedValidationErrors = {
  required: true;
  min: { min: number; actual: number };
  max: { max: number; actual: number };
  absoluteMin: { absoluteMin: number; actual: number };
  absoluteMax: { absoluteMax: number; actual: number };
  minlength: { requiredLength: number; actualLength: number };
  maxlength: { requiredLength: number; actualLength: number };
  minDate: {
    minDate: string;
    actualDate: string;
  };
  maxDate: {
    maxDate: string;
    actualDate: string;
  };
  remainingLife: { life: number; remainingLife: number };
  sameSign: { sameSignIsPositive: boolean; actualSignIsPositive: boolean; actual: number };
  between: { betweenMin: number; betweenMax: number };
  duplicateLabel: { value: string };
  codeAlreadyUsed: true;
  serverError: true;
  [key: string]: unknown;
};

export class InvalidMessageForContext<TKey extends keyof TypedValidationErrors> {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  public $implicit: TypedValidationErrors[TKey] = null!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  public invalidMessageFor: TypedValidationErrors[TKey] = null!;
}

@Directive({
  selector: '[invalidMessageFor]',
  standalone: false,
})
export class InvalidMessageForDirective<TKey extends keyof TypedValidationErrors>
  implements DoCheck
{
  private context = new InvalidMessageForContext<TKey>();
  private viewRef: EmbeddedViewRef<InvalidMessageForContext<TKey>> | null = null;

  private errorCodes: TKey[] = [];

  @Input() set invalidMessageFor(value: TKey | TKey[]) {
    if (Array.isArray(value)) {
      this.errorCodes = value;
    } else {
      this.errorCodes = [value];
    }
  }

  constructor(
    private viewContainer: ViewContainerRef,
    private templateRef: TemplateRef<InvalidMessageForContext<TKey>>,
    private renderer: Renderer2,
    @Inject(VALIDATION_CONTEXT) private validationContext: ValidationContextDirective,
  ) {}

  ngDoCheck() {
    const error =
      this.validationContext.touched &&
      this.validationContext.errors &&
      this.errorCodes
        .map(errorCode => this.validationContext.errors?.[errorCode])
        .filter(x => !!x)[0];

    if (error) {
      this.context.$implicit = this.context.invalidMessageFor = error;
      if (!this.viewRef) {
        this.viewRef = this.viewContainer.createEmbeddedView(this.templateRef, this.context);
        (this.viewRef.rootNodes as HTMLElement[]).forEach(el => {
          this.renderer.addClass(el, 'invalid-feedback');
          this.renderer.addClass(el, 'd-block');
        });
      }
    } else {
      if (this.viewRef) {
        this.viewContainer.clear();
        this.viewRef = null;
      }
    }
  }

  static ngTemplateGuard_invalidMessageFor: 'binding';

  static ngTemplateContextGuard<TKey extends keyof TypedValidationErrors>(
    _dir: InvalidMessageForDirective<TKey>,
    _ctx: unknown,
  ): _ctx is InvalidMessageForContext<TKey> {
    return true;
  }
}
